import { G } from '../config/constants';

export interface OrbitPrediction {
  points: [number, number][];
  /** Spatial trajectory in the same units and reference frame as the input. */
  points3d: [number, number, number][];
  eccentricity: number;
  apoapsis: number;
  periapsis: number;
  bound: boolean;
  timeToAp?: number;
  timeToPe?: number;
}

export function predictOrbit(
  pos: [number, number, number],
  vel: [number, number, number],
  bodyMass: number,
  soiRadius: number,
  steps: number
): OrbitPrediction {
  const mu = G * bodyMass;
  const r = Math.sqrt(pos[0] * pos[0] + pos[1] * pos[1] + pos[2] * pos[2]);
  const v2 = vel[0] * vel[0] + vel[1] * vel[1] + vel[2] * vel[2];

  const specificEnergy = v2 / 2 - mu / r;
  const a = -mu / (2 * specificEnergy);

  const hx = pos[1] * vel[2] - pos[2] * vel[1];
  const hy = pos[2] * vel[0] - pos[0] * vel[2];
  const hz = pos[0] * vel[1] - pos[1] * vel[0];
  const h = Math.sqrt(hx * hx + hy * hy + hz * hz);

  const evx = (vel[1] * hz - vel[2] * hy) / mu - pos[0] / r;
  const evy = (vel[2] * hx - vel[0] * hz) / mu - pos[1] / r;
  const evz = (vel[0] * hy - vel[1] * hx) / mu - pos[2] / r;
  const e = Math.sqrt(evx * evx + evy * evy + evz * evz);

  const bound = e < 1 && a > 0;
  const apoapsis = bound ? a * (1 + e) : Infinity;
  const semiLatusRectum = h * h / mu;
  const periapsis = semiLatusRectum / (1 + e);

  const ex = e > 1e-8 ? evx / e : pos[0] / r;
  const ey = e > 1e-8 ? evy / e : pos[1] / r;
  const ez = e > 1e-8 ? evz / e : pos[2] / r;

  const p: [number, number, number] = [ex, ey, ez];
  const q: [number, number, number] = [
    (hy * ez - hz * ey) / (h || 1),
    (hz * ex - hx * ez) / (h || 1),
    (hx * ey - hy * ex) / (h || 1),
  ];

  const pts: [number, number][] = [];
  const points3d: [number, number, number][] = [];
  const thetaMax = bound ? Math.PI : Math.acos(-1 / Math.max(e, 1.001)) * 0.98;

  // Compute time to Ap/Pe for bound orbits
  let timeToAp: number | undefined;
  let timeToPe: number | undefined;
  if (bound && e < 1e-8) {
    // Circular orbits have no unique apsides: use current position as phase zero.
    timeToPe = 2 * Math.PI * Math.sqrt(a ** 3 / mu);
    timeToAp = timeToPe / 2;
  } else if (bound) {
    const orbitPeriod = 2 * Math.PI * Math.sqrt((a * a * a) / mu);
    const dot = (pos[0] * vel[0] + pos[1] * vel[1] + pos[2] * vel[2]) / (r || 1);
    const cosTheta = (a * (1 - e * e) / r - 1) / e;
    const sinTheta = dot * Math.sqrt(a * (1 - e * e) / mu) / (e || 1);
    let trueAnomaly = Math.atan2(sinTheta, cosTheta);
    if (trueAnomaly < 0) trueAnomaly += Math.PI * 2;
    const cosE = (e + cosTheta) / (1 + e * cosTheta);
    const sinE = Math.sqrt(1 - e * e) * sinTheta / (1 + e * cosTheta);
    let E = Math.atan2(sinE, cosE);
    if (E < 0) E += Math.PI * 2;
    const meanAnomaly = E - e * sinE;
    const timeFromPe = meanAnomaly * orbitPeriod / (2 * Math.PI);
    const timeToPeOrbit = orbitPeriod - timeFromPe;
    const timeToApOrbit = (timeToPeOrbit + orbitPeriod / 2) % orbitPeriod;
    timeToPe = timeToPeOrbit;
    timeToAp = timeToApOrbit;
  }

  for (let i = 0; i <= steps; i++) {
    const theta = -thetaMax + (2 * thetaMax * i) / steps;
    const denom = 1 + e * Math.cos(theta);
    if (Math.abs(denom) < 1e-10) continue;
    const rr = semiLatusRectum / denom;
    if (isNaN(rr) || !isFinite(rr) || rr < 0) continue;
    const x = rr * (Math.cos(theta) * p[0] + Math.sin(theta) * q[0]);
    const y = rr * (Math.cos(theta) * p[1] + Math.sin(theta) * q[1]);
    const z = rr * (Math.cos(theta) * p[2] + Math.sin(theta) * q[2]);
    const dist = Math.hypot(x, y, z);
    if (dist > soiRadius * 1.5) continue;
    pts.push([x, z]);
    points3d.push([x, y, z]);
  }

  return { points: pts, points3d, eccentricity: e, apoapsis, periapsis, bound, timeToAp, timeToPe };
}
